import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Layout,
  Menu,
  Button,
  Drawer,
  Dropdown,
  Badge,
  Space,
  Tooltip,
} from "antd";
import { UserOutlined, DownOutlined, MenuOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  clearLocalStorageData,
  getLocalStorageData,
  setLocalStorageData,
} from "../../shared/commonFunction";
import { setLoginState } from "../../features/login/loginSlice";
import { getApiRequest } from "../../services/getApiRequest";
import { END_POINTS } from "../../Helper/Constant";
import { logout, setUser } from "../../features/user/userSlice";
import { setCountry } from "../../features/country/countrySlice";
import { showToast } from "../../shared/sharedComponents/ToasterMessage/ToasterMessage";
import { setLang } from "../../features/language/language";
import logo from "../../assets/images/logo.svg";
import userProfile from "../../assets/icons/user.svg";
import bellIcon from "../../assets/icons/Bell.svg";
import messageIcon from "../../assets/icons/message-icon.svg";
import myOrders from "../../assets/icons/my-order.svg";
import logOut from "../../assets/icons/logout.svg";
import coins from "../../assets/icons/header-coin.svg";
import rewards from "../../assets/icons/reward-icon.svg";
import friendRequest from "../../assets/icons/friends.svg";
import goat from "../../assets/icons/goatAvaterImage.png";
import "./Navbar.scss";
import {
  headerStyle,
  logoImageStyle,
  menuDesktopStyle,
  menuMobileStyle,
  loginButtonStyle,
  menuStyle,
  menuItemStyle,
  activeStyle,
  drawerBodyStyle,
} from "./NavbarStyles";
import message from "../../assets/images/message.svg";
import tournamentNotify from "../../assets/images/notify-bell-icon.svg";
import cup from "../../assets/images/cup.svg";
import { fetchGameTypes } from "../../features/gameType/gameTypeSlice";
import { hideLoader, showLoader } from "../../features/loader/loaderSlice";
import userImage from "../../assets/images/user-image.svg";
import useSocket from "../../hooks/useSocket";
import { addNotification } from "../../features/notifications/notificationsSlice";
import wallet from "../../assets/icons/wallet.svg";
import { addChatNotification } from "../../features/chatNotifications/chatNotificationsSlice";
import moment from "moment";
import notificationSound from "../../assets/notification-sound/notification.mp3";
import fire from "../../assets/icons/fire.svg";
import cart from "../../assets/icons/cart.svg";
import { fetchCart } from "../../features/cart/cartSlice";
import i18next from "i18next";
import { postApiRequest } from "../../services/postApiRequest";

const { Header } = Layout;

const Navbar = () => {
  // State
  const [open, setOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [chatNotificationOpen, setChatNotificationOpen] = useState(false);
  const [notification, setNotification] = useState([]);
  const [chatNotification, setChatNotification] = useState([]);
  const [friendRequestCount, setFriendRequestCount] = useState(0);

  // Refs
  const previousNotificationCountRef = useRef(0);
  const previousChatNotificationCountRef = useRef(0);
  const audioRef = useRef(new Audio(notificationSound));
  const lastSoundPlayedRef = useRef(0);

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("common");
  const socketConnection = useSocket();
  const { socket, isReady } = socketConnection;

  // Selectors
  const isLogin = useSelector((state) => state.login.isLogin);
  const lang = useSelector((state) => state.language.language);
  const userData =
    useSelector((state) => state.user.userData) ||
    JSON.parse(getLocalStorageData("user") || "{}");
  const unreadCount = useSelector((state) => state.notifications.unreadCount);
  const readCount = useSelector((state) => state.chatNotifications.readCount);
  const notifications = useSelector(
    (state) => state.notifications.notifications
  );
  const countryData = useSelector((state) => state.country.countryData);
  const chatNotifications = useSelector(
    (state) => state.chatNotifications.chatNotifications
  );
  const { items } = useSelector((state) => state.cart);
  const hasActiveEvents = useSelector((state) => state.event.hasActiveEvents);

  // Constants
  const SOUND_THROTTLE_TIME = 3000; // 3 seconds
  const todayDate = useMemo(() => moment().format("D MMM"), []);

  // Memoized menu items
  const menuItems = useMemo(
    () => [
      { key: "/", label: t("menu.home") },
      { key: `/all-tournaments`, label: t("menu.tournaments") },
      { key: `/store`, label: t("menu.store") },
      { key: "/duels/lobby", label: t("menu.one_to_one") },
      ...(hasActiveEvents ? [{ key: "/events", label: t("menu.events") }] : []),
      { key: "/leadership-board", label: t("menu.leaderboard") },
      { key: "/academy", label: t("menu.academy") },
      { key: "/contact-us", label: t("menu.contact_us") },
      { key: "/about-us", label: t("menu.about_us") },
    ],
    [t, hasActiveEvents]
  );

  // Memoized cart items count
  const cartItemsCount = useMemo(
    () => items?.cartItems?.length || 0,
    [items?.cartItems?.length]
  );

  const handleLogout = useCallback(async () => {
    try {
      const payload = { userId: getLocalStorageData("userId") || userData?._id };
      const response = await postApiRequest(END_POINTS.LOGOUT, payload);
      console.log('response: ', response);

      if (response?.success) {
        const currentLanguage = getLocalStorageData("language");
        clearLocalStorageData();
        if (currentLanguage) {
          setLocalStorageData("language", currentLanguage);
        }
        dispatch(setLoginState(false));
        dispatch(logout());
        navigate("/login");
      } else {
        showToast(response?.message || t("logout.failed"), "error");
      }
    } catch (error) {
      console.log('error: ', error);
      showToast(
        error?.response?.data?.message || error?.message || t("logout.failed"),
        "error"
      );
    }
  }, [dispatch, navigate, userData?._id, t]);

  useEffect(() => {
    if (isLogin) {
      dispatch(fetchCart(i18next.language));
    }
  }, [dispatch, isLogin]);

  const userMenuItems = useMemo(
    () => [
      {
        label: (
          <div className="user-email-username">
            <span>{userData?.email}</span>
            <br />

            <span className="user-name">{userData?.userName} </span>
            {userData?.winStreak > 0 && (
              <span>
                <Tooltip title={t("one_to_one.win_streak")}>
                  <img src={fire} alt="fire" loading="lazy" />
                </Tooltip>
                {userData?.winStreak}
              </span>
            )}

          </div>
        ),
        icon: (
          <div>
            <img
              src={userData?.avatarUrl || "https://res.cloudinary.com/dnojqxptc/image/upload/v1779699920/bes00kjn7qpvtgba8b77.jpg"}
              alt="avatar-img"
              className="user-profile-img"
              loading="lazy"
            />
            {userData?.goatGifts > 0 && (
              <span className="goat-image">
                <Tooltip title={t("user_profile.goat_gifts")}>
                  <img src={goat} alt="goat" loading="lazy" />
                </Tooltip>
                <span className="goat-number">{userData?.goatGifts}</span>
              </span>
            )}
          </div>
        ),
      },
      {
        label: (
          <Link
            to="/profile"
            className={location.pathname === "/profile" ? "highlight" : ""}
          >
            {t("dropdown.myProfile")}
          </Link>
        ),
        icon: (
          <img
            src={userProfile}
            alt="avatar-img"
            className={location.pathname === "/profile" ? "highlight" : ""}
            loading="lazy"
          />
        ),
        className: "my-profile-item",
      },
      {
        label: (
          <Link
            to="/friends"
            state={{ defaultTab: "myFriends" }}
            className={location.pathname === "/friends" ? "highlight" : ""}
          >
            {t("dropdown.myFriends")}
          </Link>
        ),
        icon: (
          <img
            src={friendRequest}
            alt="friends-img"
            className={location.pathname === "/friends" ? "highlight" : ""}
            loading="lazy"
          />
        ),
        className: "my-profile-item",
      },
      // {
      //   label: (
      //     <Link
      //       to="/achievements"
      //       className={location.pathname === "/achievements" ? "highlight" : ""}
      //     >
      //       {t("dropdown.achievement")}
      //     </Link>
      //   ),
      //   icon: (
      //     <img
      //       src={achievement}
      //       alt="achievement-img"
      //       className={location.pathname === "/achievements" ? "highlight" : ""}
      //       loading="lazy"
      //     />
      //   ),
      //   className: "my-profile-item",
      // },
      {
        label: (
          <Link
            to={`/my-tournaments`}
            className={
              location.pathname === "/my-tournaments" ? "highlight" : ""
            }
          >
            {t("dropdown.tournament")}
          </Link>
        ),
        icon: (
          <img
            src={cup}
            alt="avatar-img"
            className={
              location.pathname === "/my-tournaments" ? "highlight" : ""
            }
            loading="lazy"
          />
        ),
      },
      {
        label: (
          <Link
            to="/messages"
            className={location.pathname === "/messages" ? "highlight" : ""}
          >
            {t("dropdown.message")}
          </Link>
        ),
        icon: (
          <img
            src={messageIcon}
            alt="avatar-img"
            className={location.pathname === "/messages" ? "highlight" : ""}
            loading="lazy"
          />
        ),
      },

      {
        label: (
          <Link
            to="/my-orders"
            className={location.pathname === "/my-orders" ? "highlight" : ""}
          >
            {t("dropdown.my_orders")}
          </Link>
        ),
        icon: (
          <img
            src={myOrders}
            alt="avatar-img"
            className={
              location.pathname === "/my-orders"
                ? "highlight user-order-avatar"
                : "user-order-avatar"
            }
            loading="lazy"
          />
        ),
      },

      {
        label: (
          <Link
            to="/rewards"
            className={location.pathname === "/rewards" ? "highlight" : ""}
          >
            {t("dropdown.rewards")}
          </Link>
        ),
        icon: (
          <img
            src={rewards}
            alt="avatar-img"
            className={
              location.pathname === "/rewards"
                ? "highlight user-order-avatar"
                : "user-order-avatar"
            }
            loading="lazy"
          />
        ),
      },

      {
        label: (
          <Link
            to="/wallet"
            className={location.pathname === "/wallet" ? "highlight" : ""}
          >
            {t("dropdown.wallet")}
          </Link>
        ),
        icon: (
          <img
            src={wallet}
            alt="avatar-img"
            className={location.pathname === "/wallet" ? "highlight" : ""}
            loading="lazy"
          />
        ),
      },
      { type: "divider" },
      {
        label: t("dropdown.logout"),
        icon: (
          <img
            src={logOut}
            alt="logout-img"
            className="user-profile-avatar"
            loading="lazy"
          />
        ),
        onClick: handleLogout,
      },
    ],
    [userData, location.pathname, t, handleLogout]
  );

  // API functions
  const fetchUserData = useCallback(async () => {
    dispatch(showLoader());
    try {
      const response = await getApiRequest(END_POINTS.GET_USER_DATA);
      console.log("response:for user", response);
      if (response.success) {
        dispatch(setUser(response.user));
        setLocalStorageData("user", JSON.stringify(response.user));
      }
    } catch (error) {
      showToast(error?.response?.data?.message || error?.error?.message, "error");
    } finally {
      dispatch(hideLoader());
    }
  }, [dispatch]);

  const fetchCountryData = useCallback(async () => {
    dispatch(showLoader());
    try {
      const response = await getApiRequest(END_POINTS.GET_ALL_COUNTRY_CODE);
      if (response.success) {
        dispatch(setCountry(response.countries));
      }
    } catch (error) {
      console.log(error);
      showToast(error.message, "error");
    } finally {
      dispatch(hideLoader());
    }
  }, [dispatch]);

  // Sound management
  const playNotificationSound = useCallback(async () => {
    const now = Date.now();
    if (now - lastSoundPlayedRef.current > SOUND_THROTTLE_TIME) {
      try {
        audioRef.current.currentTime = 0; // Reset audio to start
        await audioRef.current.play();
        console.log("Notification sound played");
        lastSoundPlayedRef.current = now;
      } catch (err) {
        console.error("Sound error:", err);
      }
    }
  }, []);

  // Socket event handlers
  const handleNotifications = useCallback(
    async (data) => {
      dispatch(addNotification(data));

      const newNotificationCount = data?.notifications?.length || 0;
      if (
        newNotificationCount > previousNotificationCountRef.current &&
        newNotificationCount > 0
      ) {
        await playNotificationSound();
      }
      previousNotificationCountRef.current = newNotificationCount;
    },
    [dispatch, playNotificationSound]
  );

  const handleChatNotifications = useCallback(
    (data) => {
      dispatch(addChatNotification(data?.chatNotifications));

      const newChatNotificationCount = data?.chatNotifications?.length || 0;
      if (
        newChatNotificationCount > previousChatNotificationCountRef.current &&
        newChatNotificationCount > 0
      ) {
        playNotificationSound();
      }
      previousChatNotificationCountRef.current = newChatNotificationCount;
    },
    [dispatch, playNotificationSound]
  );

  const getUserDetails = useCallback(
    (data) => {
      if (data?.success) {
        dispatch(setUser(data?.user));
        setLocalStorageData("user", JSON.stringify(data?.user));
      }

    },
    [dispatch]
  );

  const handleFriendRequestsCount = useCallback((data) => {
    console.log('count data: ', data);
    const count =
      typeof data === "number" ? data : data?.count ?? data?.friendRequestsCount ?? 0;
    setFriendRequestCount(count);
  }, []);

  // Event handlers
  const changeLanguage = useCallback(
    (lng) => {
      setLocalStorageData("language", lng);
      i18n.changeLanguage(lng);
      document.body.dir = lng === "ar" ? "rtl" : "ltr";
      if (lng === "ar") {
        document.body.classList.add("font-arabic");
        document.body.classList.remove("font-english");
      } else {
        document.body.classList.add("font-english");
        document.body.classList.remove("font-arabic");
      }
      dispatch(setLang(lng));

      // Explicitly fetch notifications with new language
      if (isLogin && socket && isReady && userData?._id) {
        socket.emit("get-notifications", {
          userId: userData._id,
          language: lng,
        });
      }
    },
    [i18n, dispatch, isLogin, socket, isReady, userData?._id]
  );

  const handleMenuClick = useCallback((e) => {
    if (e.key === "3") {
      setNotificationOpen(false);
    }
  }, []);

  const handleChatMenuClick = useCallback((e) => {
    if (e.key === "3") {
      setChatNotificationOpen(false);
    }
  }, []);

  const handleOpenChange = useCallback((nextOpen, info) => {
    if (info?.source === "trigger" || nextOpen) {
      setNotificationOpen(nextOpen);
    }
  }, []);

  const handleChatOpenChange = useCallback(
    (nextOpen) => {
      if (nextOpen && chatNotification?.length === 0) {
        navigate("/messages");
      } else {
        setChatNotificationOpen(nextOpen);
      }
    },
    [navigate, chatNotification?.length]
  );

  const handleNotificationBarClick = useCallback(() => {
    console.log("handleNotificationBarClick: ");
    if (socket && isReady && userData?._id) {
      socket.emit("see-notifications", {
        userId: userData._id,
        language: i18n.language,
      });
    }
  }, [socket, isReady, userData?._id, i18n.language]);

  const handleResize = useCallback(() => {
    if (window.innerWidth > 1200) {
      setOpen(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    const token = getLocalStorageData("token");
    if (token) {
      // dispatch(setLoginState(true));
      dispatch(fetchGameTypes());
      fetchUserData();
    }
  }, [dispatch, fetchUserData]);

  useEffect(() => {
    if (!countryData) {
      fetchCountryData();
    }
  }, [countryData, fetchCountryData]);

  useEffect(() => {
    if (!socket || !isReady || !userData?._id || !isLogin) return;

    const payload = {
      userId: userData._id,
      language: i18n.language,
    };

    socket.emit("get-notifications", payload);

    // Ensure one active listener per event, even after re-renders/reconnects.
    socket.off("notifications", handleNotifications);
    socket.off("chat-notifications", handleChatNotifications);
    socket.off("get-user", getUserDetails);
    socket.off("friend-requests-count", handleFriendRequestsCount);

    socket.on("notifications", handleNotifications);
    socket.on("chat-notifications", handleChatNotifications);
    socket.on("get-user", getUserDetails);
    socket.on("friend-requests-count", handleFriendRequestsCount);

    return () => {
      socket.off("notifications", handleNotifications);
      socket.off("chat-notifications", handleChatNotifications);
      socket.off("get-user", getUserDetails);
      socket.off("friend-requests-count", handleFriendRequestsCount);
    };
  }, [
    socket,
    isReady,
    userData?._id,
    isLogin,
    i18n.language,
    handleNotifications,
    handleChatNotifications,
    getUserDetails,
    handleFriendRequestsCount,
  ]);

  useEffect(() => {
    if (notifications && notifications.length > 0) {
      const notificationItems = notifications.map((notification) => {
        const baseNotificationData = {
          key: notification._id,
          className: "notification-class",
        };

        if (
          notification.notificationFor === "group-chat" ||
          notification.notificationFor === "tournament-chat"
        ) {
          return {
            ...baseNotificationData,
            key: `${notification._id}-${notification?.messageNotification?.sender?._id}`,
            icon: (
              <img
                src={
                  notification?.messageNotification?.sender?.avatarUrl ||
                  userImage
                }
                alt="chat-avatar-img"
                className="chat-avatar-img"
                style={{ borderRadius: "50%" }}
                loading="lazy"
              />
            ),
            label: (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div className="chat-sender-name">
                    {notification?.messageNotification?.sender?.userName} (
                    {notification?.tournamentId?.name})
                  </div>
                  <div
                    className="chat-last-message"
                    style={{
                      opacity: 0.8,
                      marginBlock: "5px",
                      fontSize: "16px",
                    }}
                  >
                    {notification?.messageNotification?.lastMessage}
                  </div>
                </div>
                <div>
                  <p style={{ opacity: "0.8", padding: "5px", margin: "0" }}>
                    {moment(
                      notification?.messageNotification?.lastMessageAt
                    ).format("D MMM") === todayDate
                      ? moment(
                        notification?.messageNotification?.lastMessageAt
                      ).format("h:mmA")
                      : moment(
                        notification?.messageNotification?.lastMessageAt
                      ).format("D MMM, h:mmA")}
                  </p>
                  {notification?.messageNotification?.unreadCount > 0 && (
                    <p className="chat-unread-count" style={{ margin: "0" }}>
                      {notification?.messageNotification?.unreadCount}
                    </p>
                  )}
                </div>
              </div>
            ),
            onClick: () => {
              const fromType =
                notification.notificationFor === "group-chat"
                  ? "group-chat"
                  : "tournament-chat";
              navigate(
                `/register-featured/${notification?.tournamentId?._id}?userId=${notification?.userId || null
                }`,
                { state: { from: fromType, notificationId: notification._id } }
              );
              setNotificationOpen(false);
            },
          };
        } else if (notification.notificationFor === "one-vs-one") {
          return {
            ...baseNotificationData,
            icon: (
              <img
                src={tournamentNotify}
                alt="tournament-notify-img"
                loading="lazy"
              />
            ),
            label: <div className="congratulation">{notification.text}</div>,
            onClick: () => {
              navigate("/one-to-one", {
                state: { from: "one-to-one", notificationId: notification._id },
              });
              setNotificationOpen(false);
            },
          };
        } else {
          return {
            ...baseNotificationData,
            icon: (
              <img
                src={tournamentNotify}
                alt="tournament-notify-img"
                loading="lazy"
              />
            ),
            label: <div className="congratulation">{notification.text}</div>,
            onClick: () => {
              navigate(
                notification?.tournamentId
                  ? `/register-featured/${notification?.tournamentId?._id}`
                  : "/all-tournaments",
                {
                  state: {
                    fromNotification: true,
                    notificationId: notification._id,
                  },
                }
              );
              setNotificationOpen(false);
            },
          };
        }
      });

      setNotification(notificationItems);
    } else {
      setNotification([]);
    }
  }, [notifications, navigate, todayDate]);

  useEffect(() => {
    if (chatNotifications && chatNotifications.length > 0) {
      const chatNotificationItems = chatNotifications
        .filter((notification) => notification?.sender?._id)
        .map((notification) => ({
          key: notification.sender._id,
          icon: (
            <img
              src={notification?.sender?.avatarUrl || userImage}
              alt="chat-avatar-img"
              className="chat-avatar-img"
              style={{ borderRadius: "50%" }}
              loading="lazy"
            />
          ),
          label: (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div className="chat-sender-name">
                  {notification?.sender?.userName}
                </div>
                <div
                  className="chat-last-message"
                  style={{ opacity: 0.8, marginBlock: "5px", fontSize: "16px" }}
                >
                  {notification?.lastMessage}
                </div>
              </div>
              <div>
                <p style={{ opacity: "0.8", padding: "5px", margin: "0" }}>
                  {moment(notification?.lastMessageAt).format("D MMM") ===
                    todayDate
                    ? moment(notification?.lastMessageAt).format("h:mmA")
                    : moment(notification?.lastMessageAt).format(
                      "D MMM, h:mmA"
                    )}
                </p>
                {notification.unreadCount > 0 && (
                  <p className="chat-unread-count" style={{ margin: "0" }}>
                    {notification.unreadCount}
                  </p>
                )}
              </div>
            </div>
          ),
          onClick: () => {
            setLocalStorageData("isChatUser", notification.sender._id);
            navigate("/messages");
            setChatNotificationOpen(false);
          },
        }));

      setChatNotification(chatNotificationItems);
    } else {
      setChatNotification([]);
    }
  }, [chatNotifications, navigate, todayDate]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    const storedLanguage = getLocalStorageData("language");
    dispatch(setLang(storedLanguage || "en"));
  }, [dispatch]);

  return (
    <Header style={headerStyle} className="header-main">
      <div className="logo">
        <Link to="/">
          <img
            src={logo}
            alt="Company Logo"
            style={logoImageStyle}
            loading="lazy"
          />
        </Link>
      </div>

      <div className="menu-desktop" style={menuDesktopStyle}>
        <Menu
          mode="horizontal"
          selectedKeys={[location.pathname]}
          style={menuStyle}
          items={menuItems.map((item) => ({
            key: item.key,
            label: <Link to={item.key}>{item.label}</Link>,
            style: location.pathname === item.key ? activeStyle : menuItemStyle,
          }))}
        />
      </div>

      <div style={menuItemStyle} className="lanuage-selection-division desktop">
        <span
          className={lang === "ar" ? "selected-language" : "language"}
          onClick={() => changeLanguage("ar")}
        >
          Ar
        </span>
        /
        <span
          className={lang === "en" ? "selected-language" : "language"}
          onClick={() => changeLanguage("en")}
        >
          En
        </span>
      </div>

      {!isLogin ? (
        <Button
          type="primary"
          icon={<UserOutlined style={{ color: "black", fontSize: "20px" }} />}
          style={loginButtonStyle}
          className="sign-up"
        >
          <Link className="login-signup-btn" to="/login">
            {t("menu.login")}
          </Link>
          /
          <Link className="login-signup-btn" to="/sign-up">
            {t("menu.sign_up")}
          </Link>
        </Button>
      ) : (
        <div className="noti-join-tou">
          <Tooltip title={t("wallet.coins")}>
            <Link to="/wallet" className="wallet-link">
              <div className="coins-icons">
                <span>
                  {Math.floor(
                    userData?.totalCoins && userData?.totalCoins > 0
                      ? userData?.totalCoins
                      : 0
                  )}
                </span>
                <span style={{ display: "flex" }}>
                  <img
                    src={coins}
                    alt="loading-coin-icon"
                    style={{ margin: "0 5px" }}
                    loading="lazy"
                  />
                </span>
              </div>
            </Link>
          </Tooltip>

          <div className="noti">
            <Tooltip title={t("dropdown.myFriends")}>
              <Link to="/friends" state={{ defaultTab: "friendRequests" }}>
                <Badge count={friendRequestCount} className="notification-bell">
                  <img
                    className="friends-request-icon"
                    src={friendRequest}
                    alt="user-profile-icon"
                    loading="lazy"
                  />
                </Badge>
              </Link>
            </Tooltip>
          </div>

          <div className="noti">
            <Dropdown
              menu={{ items: notification, onClick: handleMenuClick }}
              onOpenChange={handleOpenChange}
              open={notificationOpen}
              trigger={["click"]}
              arrow
              overlayClassName="notification"
            >
              <Space onClick={handleNotificationBarClick}>
                <Badge count={unreadCount} className="notification-bell">
                  <img src={bellIcon} alt="bell-img" loading="lazy" />
                </Badge>
              </Space>
            </Dropdown>
          </div>

          <div className="noti">
            <Dropdown
              menu={{ items: chatNotification, onClick: handleChatMenuClick }}
              onOpenChange={handleChatOpenChange}
              open={chatNotificationOpen}
              trigger={["click"]}
              arrow
              overlayClassName="notification"
            >
              <Space>
                <Badge count={readCount} className="notification-bell">
                  <img src={message} alt="message-img" loading="lazy" />
                </Badge>
              </Space>
            </Dropdown>
          </div>
          <div className="noti">
            <Tooltip title={t("cart.title")}>
              <Badge count={cartItemsCount} showZero>
                <Link to="/my-cart">
                  <img className="cart-img-main" src={cart} alt="add-to-cart" />
                </Link>
              </Badge>
            </Tooltip>
          </div>

          <div className="user-profile-drpdown">
            <Dropdown
              trigger={["click"]}
              menu={{ items: userMenuItems }}
              className="user-detail-dropdown"
              overlayClassName="user-profile-drpdown"
            >
              <Button>
                <Space>
                  <div className="user-name-navbar">{userData?.userName}</div>

                  <img
                    src={userData?.avatarUrl || "https://res.cloudinary.com/dnojqxptc/image/upload/v1779699920/bes00kjn7qpvtgba8b77.jpg"}
                    alt="user-profile"
                    className="user-profile-img"
                    loading="lazy"
                  />

                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        </div>
      )}

      <Drawer
        title={t("menu.menu")}
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        styles={{ body: drawerBodyStyle }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          style={menuStyle}
          items={menuItems.map((item) => ({
            key: item.key,
            label: <Link to={item.key}>{item.label}</Link>,
            style: location.pathname === item.key ? activeStyle : menuItemStyle,
            onClick: () => setOpen(false),
          }))}
        />
      </Drawer>

      <div style={menuItemStyle} className="lanuage-selection-division mobile">
        <span
          className={lang === "ar" ? "selected-language" : "language"}
          onClick={() => changeLanguage("ar")}
        >
          Ar
        </span>
        /
        <span
          className={lang === "en" ? "selected-language" : "language"}
          onClick={() => changeLanguage("en")}
        >
          En
        </span>
      </div>

      <div className="menu-mobile" style={menuMobileStyle}>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setOpen(true)}
          style={menuItemStyle}
        />
      </div>
    </Header>
  );
};

export default Navbar;
